/**
 * Application state (Svelte 5 runes).
 *
 * The local mirror is the source of truth for what is on screen. Writes are
 * optimistic: they hit IndexedDB and the UI first, then drain to the Worker.
 * That is what makes the app usable offline and instant when online.
 */

import type { Client, Entry, EntryInput } from '$shared/types';
import { toMonthKey } from '$shared/dates';
import { api, ApiFailure } from './api';
import * as db from './db.svelte';

type Status = 'loading' | 'signed-out' | 'ready';

class Store {
  status = $state<Status>('loading');
  email = $state<string | null>(null);

  clients = $state<Client[]>([]);
  activeClientId = $state<string | null>(null);
  entries = $state<Entry[]>([]);

  /** Entry ids with unsent work — rendered as a small pending dot. */
  pending = $state<Set<string>>(new Set());
  online = $state(navigator.onLine);
  syncing = $state(false);
  error = $state<string | null>(null);

  activeClient = $derived(this.clients.find((c) => c.id === this.activeClientId) ?? null);

  /** Notes already used for this client, newest first — feeds the autocomplete. */
  noteSuggestions = $derived.by(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const e of this.entries) {
      const note = e.notes?.trim();
      if (note && !seen.has(note.toLowerCase())) {
        seen.add(note.toLowerCase());
        out.push(note);
      }
      if (out.length >= 60) break;
    }
    return out;
  });

  todayHours = $derived.by(() => {
    const t = new Date().toISOString().slice(0, 10);
    return this.entries.filter((e) => e.date === t).reduce((sum, e) => sum + e.hours, 0);
  });

  // -------------------------------------------------------------------------
  // Boot
  // -------------------------------------------------------------------------

  private initialized = false;

  async init(): Promise<void> {
    // Called from an effect, so guard against a second run wiring up duplicate
    // listeners.
    if (this.initialized) return;
    this.initialized = true;

    // Paint from cache before touching the network, so an offline launch is
    // immediately useful and an online one has no visible loading gap.
    const cached = await db.readCachedClients();
    if (cached.length) {
      this.clients = cached;
      this.activeClientId = (await db.getMeta<string>('activeClientId')) ?? cached[0].id;
      await this.loadLocalEntries();
    }

    window.addEventListener('online', () => {
      this.online = true;
      void this.sync();
    });
    window.addEventListener('offline', () => (this.online = false));
    // Coming back to the app is the moment stale data matters most — and the
    // owner may have hand-edited the sheet in the meantime.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void this.sync();
    });

    try {
      const me = await api.me();
      this.email = me.email;
      this.status = 'ready';
      await this.sync();
    } catch (err) {
      if (err instanceof ApiFailure && err.status === 401) {
        this.status = cached.length ? 'ready' : 'signed-out';
        // Cached data stays readable offline or after a session lapse; writes
        // will queue and drain once signed in again.
        if (cached.length) this.error = 'Signed out — reconnect to sync.';
      } else {
        this.status = cached.length ? 'ready' : 'signed-out';
        this.error = err instanceof Error ? err.message : String(err);
      }
    }
  }

  private async loadLocalEntries(): Promise<void> {
    if (!this.activeClientId) return;
    this.entries = await db.readEntries(this.activeClientId);
    this.pending = await db.pendingIds();
  }

  // -------------------------------------------------------------------------
  // Sync
  // -------------------------------------------------------------------------

  async sync(): Promise<void> {
    if (this.syncing || !this.online || this.status === 'signed-out') return;
    this.syncing = true;
    try {
      await this.drainQueue();

      const clients = await api.listClients();
      this.clients = clients;
      await db.cacheClients(clients);

      if (!this.activeClientId && clients.length) {
        this.activeClientId = clients[0].id;
      }
      if (this.activeClientId) {
        const fresh = await api.listEntries(this.activeClientId);
        await db.replaceClientEntries(this.activeClientId, fresh);
        await this.loadLocalEntries();
      }
      this.error = null;
    } catch (err) {
      this.handleError(err);
    } finally {
      this.syncing = false;
    }
  }

  /**
   * Send queued ops in order, stopping at the first failure so later ops that
   * may depend on it are not applied out of sequence.
   */
  private async drainQueue(): Promise<void> {
    for (const op of await db.readQueue()) {
      if (op.seq === undefined) continue;
      try {
        if (op.kind === 'create') {
          // The id was generated locally, so a retry after a dropped response
          // updates the existing row rather than creating a duplicate.
          await api.createEntry(op.entry);
        } else if (op.kind === 'update') {
          await api.updateEntry(op.id, { ...op.patch, clientId: op.clientId });
        } else {
          await api.deleteEntry(op.clientId, op.id);
        }
        await db.dequeue(op.seq);
      } catch (err) {
        // A deleted-upstream row should not wedge the queue forever.
        if (err instanceof ApiFailure && err.status === 404) {
          await db.dequeue(op.seq);
          continue;
        }
        throw err;
      }
    }
    this.pending = await db.pendingIds();
  }

  private handleError(err: unknown): void {
    if (err instanceof ApiFailure && (err.reauth || err.status === 401)) {
      this.error = 'Google session expired — reconnect to sync.';
      this.email = null;
      return;
    }
    this.error = err instanceof Error ? err.message : String(err);
  }

  // -------------------------------------------------------------------------
  // Mutations — local first, always
  // -------------------------------------------------------------------------

  async selectClient(clientId: string): Promise<void> {
    this.activeClientId = clientId;
    await db.setMeta('activeClientId', clientId);
    await this.loadLocalEntries();
    void this.sync();
  }

  async addEntry(input: Omit<EntryInput, 'clientId'> & { clientId?: string }): Promise<void> {
    const clientId = input.clientId ?? this.activeClientId;
    if (!clientId) throw new Error('Pick a client first');

    const now = new Date().toISOString();
    const entry: Entry = {
      id: crypto.randomUUID(),
      clientId,
      date: input.date,
      hours: input.hours,
      notes: input.notes ?? '',
      month: toMonthKey(input.date),
      createdAt: now,
      updatedAt: now,
    };

    await db.putEntry(entry);
    await db.enqueue({ kind: 'create', entry });
    if (clientId === this.activeClientId) await this.loadLocalEntries();
    void this.sync();
  }

  async editEntry(id: string, patch: Partial<Entry>): Promise<void> {
    const current = this.entries.find((e) => e.id === id);
    if (!current) return;

    const updated: Entry = {
      ...current,
      ...patch,
      month: patch.date ? toMonthKey(patch.date) : current.month,
      updatedAt: new Date().toISOString(),
    };

    await db.putEntry(updated);
    await db.enqueue({ kind: 'update', id, clientId: current.clientId, patch });
    await this.loadLocalEntries();
    void this.sync();
  }

  async removeEntry(id: string): Promise<void> {
    const current = this.entries.find((e) => e.id === id);
    if (!current) return;

    await db.removeEntry(id);
    await db.enqueue({ kind: 'delete', id, clientId: current.clientId });
    await this.loadLocalEntries();
    void this.sync();
  }

  async addClient(name: string, opts: Partial<Client> = {}): Promise<Client> {
    // Creating a client provisions a spreadsheet, so this one genuinely needs
    // the network — no optimistic path.
    const client = await api.createClient({ name, ...opts });
    this.clients = [...this.clients, client];
    await db.cacheClients(this.clients);
    await this.selectClient(client.id);
    return client;
  }

  async saveClient(id: string, patch: Partial<Client>): Promise<void> {
    const updated = await api.updateClient(id, patch);
    this.clients = this.clients.map((c) => (c.id === id ? updated : c));
    await db.cacheClients(this.clients);
  }

  /** Pull a client's full history — reports need more than the recent window. */
  async loadAllEntries(clientId: string): Promise<Entry[]> {
    if (this.online) {
      try {
        const fresh = await api.listEntries(clientId);
        await db.replaceClientEntries(clientId, fresh);
      } catch (err) {
        this.handleError(err);
      }
    }
    return db.readEntries(clientId);
  }
}

export const store = new Store();
